<template>
  <div class="flex w-full justify-center">
    <div class="m-4 w-1/2 min-w-min max-w-4xl text-black">
      <Breadcrumb :breadcrumbs="breadcrumbs" class="mb-4"/>
      <div class="flex pb-4">
        <div class="grow">
          <span v-show="!current_id" class="text-2xl font-bold">{{ t( 'Create group' ) }}</span>
          <span v-show="current_id" class="text-2xl font-bold">{{ t( 'Update group' ) }}</span>
        </div>
        <div class="flex gap-2">
          <LoadingButton v-if="current_id && is_deleteable && is_delete_auth()" :label="t( 'Delete' )" ref="delete_btn" @click="click_to_delete()" colors="bg-red-600 text-white hover:bg-red-700"></LoadingButton>
          <LoadingButton v-if="is_create_or_update_auth()" :label="t( 'Save' )" ref="save_btn" @click="click_to_save()"></LoadingButton>
        </div>
      </div>
      <!-- Name -->
      <div class="mb-4 rounded-md shadow-md">
        <label class="mb-2 block bg-gray-100 p-2 font-bold" for="name">{{ t( 'Name' ) }}</label>
        <InputTextRequired ref="input_name" name="name" v-model="input.name" :show_required="input_name_required" @change="input_name_required = false" :key="key_rerender"></InputTextRequired>
      </div>
      <!-- Users -->
      <div class="mb-4 rounded-md shadow-md">
        <div class="flex w-full items-center bg-gray-100">
          <label class="block grow p-2 font-bold" for="name">{{ t( 'Users' ) }}
            <span class="inline-flex items-center rounded-md bg-green-800 px-1.5 py-0.5 text-xs font-medium text-white ring-1 ring-inset ring-indigo-700/10">{{ user_items.length }}</span>
          </label>
          <div v-if="is_create_or_update_auth()">
            <IconEditButton :tooltip="t( 'Add new users' )" @click="add_users">
              <PlusBoxIcon></PlusBoxIcon>
            </IconEditButton>
          </div>
        </div>
        <EasyDataTable
        buttons-pagination alternating
        table-class-name="customize-table"
        :empty-message="t( 'No Available Data' )"
        :headers="user_headers"
        :items="user_items"
        >
          <template #item-operation="item">
            <div class="operation-wrapper">
              <DeleteOutlineIcon v-if="is_create_or_update_auth()" @click="remove_user(item)"></DeleteOutlineIcon>
            </div>
          </template>
        </EasyDataTable>
      </div>
      <!-- Rights -->
      <div class="mb-4 rounded-md shadow-md">
        <div class="flex w-full items-center bg-gray-100">
          <label class="block grow p-2 font-bold" for="name">{{ t( 'Rights' ) }}
            <span class="inline-flex items-center rounded-md bg-green-800 px-1.5 py-0.5 text-xs font-medium text-white ring-1 ring-inset ring-indigo-700/10">{{ right_items.length }}</span>
          </label>
          <div v-if="is_create_or_update_auth()">
            <IconEditButton :tooltip="t( 'Add new rights' )" @click="add_rights">
              <PlusBoxIcon></PlusBoxIcon>
            </IconEditButton>
          </div>
        </div>
        <EasyDataTable
          buttons-pagination alternating hide-header
          table-class-name="customize-table"
          :empty-message="t( 'No Available Data' )"
          :headers="right_headers"
          :items="right_items"
        >
          <template #item-operation="item">
            <div class="operation-wrapper">
              <DeleteOutlineIcon v-if="is_create_or_update_auth()" @click="remove_rights(item)"></DeleteOutlineIcon>
            </div>
          </template>
        </EasyDataTable>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useModal } from 'vue-final-modal';
import { useRoute } from 'vue-router';
import router from '@/router';
import store from '@/store';
import axios from '@/features/axios';
import utils from '@/features/utils';
import IconEditButton from '@/components/buttons/IconEditButton.vue';
import AddRightsModal from '@/components/modal/AddRightsModal.vue';
import AddUsersModal from '@/components/modal/AddUsersModal.vue';
import ConfirmModal from '@/components/modal/ConfirmModal.vue';
import API_RIGHTS from '@/constants/rights.constants';

import DeleteOutlineIcon from 'vue-material-design-icons/DeleteOutline.vue';
import PlusBoxIcon from 'vue-material-design-icons/PlusBox.vue';

export default {
  name: 'group-item-view',
  components: {
    IconEditButton,
    DeleteOutlineIcon,
    PlusBoxIcon
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const current_id = utils.get_params_id( route.params.id );
    const breadcrumbs = ref ( [
      { label: t( 'Administration' ) },
      { label: t( 'Groups' ), route: '/groups' }
    ] );
    const input = ref( { name: '' } );
    const save_btn = ref( null );
    const delete_btn = ref( null );
    const input_name = ref( null );
    const input_name_required = ref( null );
    const right_headers = ref( [
      { text: t( 'Name' ), value: 'label', sortable: true },
      { text: '', value: 'operation', sortable: false, width: 20 }
    ] );
    const user_headers = ref( [
      { text: t( 'Lastname' ), value: 'lastname', sortable: true },
      { text: t( 'Firstname' ), value: 'firstname', sortable: true },
      { text: '', value: 'operation', sortable: false, width: 20 }
    ] );
    const right_items = ref( [] );
    const user_items = ref( [] );
    const key_rerender = ref ( 0 );
    const is_deleteable = ref( false ) ;

    const force_init_rerender = () => {
      key_rerender.value += 1;
    };

    const initialize = () => {
      if ( current_id ) {
        axios.get( 'api/right/', { headers: { 'auth-token': store.getters.getToken } } )
          .then( response => {
            if ( response.status == 200 ) {
              let rights = response.data;
              axios.get( 'api/group/' + current_id, { headers: { 'auth-token': store.getters.getToken } } )
                .then( async response => {
                  if ( response.status == 200 ) {
                    input.value.name = response.data.name;
                    is_deleteable.value = response.data.is_deleteable;
                    right_items.value = response.data.rights.map( item => {
                      let right = rights.filter( it => it.key == item )[ 0 ];
                      return { value: right.key, label: right.name };
                    } );
                    for await ( const id_users of response.data.users ) {
                      axios.get( 'api/user/' + id_users, { headers: { 'auth-token': store.getters.getToken } } )
                        .then( async response => {
                          if ( response.status == 200 ) {
                            user_items.value.push( { value: id_users, label: utils.get_user_fullname( response.data ), ...response.data } );
                          } else {
                            utils.show_alert_from_resp( t, response );
                            router.push( '/groups' );
                          }
                        } )
                        .catch( e => {
                          analyse_catch( e );
                        } );
                    }
                    force_init_rerender();
                  } else {
                    utils.show_alert_from_resp( t, response );
                    router.push( '/groups' );
                  }
                } )
                .catch( e => {
                  analyse_catch( e );
                } );
            } else {
              utils.show_alert_from_resp( t, response );
              router.push( '/groups' );
            }
          } )
          .catch( e => {
            analyse_catch( e );
          } );
      }
    };

    const click_to_save = () => {
      if ( typeof ( input.value.name ) != 'string' || input.value.name.trim() == '' ) {
        input_name_required.value = true;
        input_name.value.focus();
        save_btn.value.stop_loading();
        return;
      }

      input_name_required.value = false;

      let rights = right_items.value.map( item => { return item.value; } );
      let users = user_items.value.map( item => { return item.value; } );

      if ( current_id ) {
        update_group( input.value.name, rights, users );
      } else {
        create_new_group( input.value.name, rights, users );
      }
    };

    const create_new_group = ( name, rights, users ) => {
      let data = {
        name: name,
        rights: rights.length > 0 ? rights : undefined,
        users: users.length > 0 ? users : undefined
      };

      axios.post( 'api/group/', data, { headers: { 'auth-token': store.getters.getToken } } )
        .then( response => {
          if ( response.status == 200 ) {
            router.push( '/groups' );
          } else {
            utils.show_alert_from_resp( t, response );
          }
          save_btn.value.stop_loading();
        } )
        .catch( e => {
          save_btn.value.stop_loading();
          analyse_catch( e );
        } );
    };

    const update_group = ( name, rights, users ) => {
      let data = {
        name: name,
        rights: { set: rights },
        users: { set: users }
      };

      axios.put( 'api/group/' + current_id, data, { headers: { 'auth-token': store.getters.getToken } } )
        .then( response => {
          if ( response.status == 201 ) {
            router.push( '/groups' );
          } else {
            utils.show_alert_from_resp( t, response );
          }
          save_btn.value.stop_loading();
        } )
        .catch( e => {
          save_btn.value.stop_loading();
          analyse_catch( e );
        } );
    };

    const click_to_delete = () => {
      confirm_delete_modal.open();
    };

    const confirm_delete_modal = useModal( {
      component: ConfirmModal,
      attrs: {
        title: t( 'Delete group' ),
        message: t( 'Confirm delete group' ),
        onConfirm() {
          delete_group();
          confirm_delete_modal.close();
        },
        onCancel() {
          confirm_delete_modal.close();
          delete_btn.value.stop_loading();
        }
      }
    } );

    const delete_group = () => {
      axios.delete( 'api/group/' + current_id, { headers: { 'auth-token': store.getters.getToken } } )
        .then( response => {
          if ( response.status == 201 ) {
            router.push( '/groups' );
          } else {
            utils.show_alert_from_resp( t, response );
          }
          delete_btn.value.stop_loading();
        } )
        .catch( e => {
          delete_btn.value.stop_loading();
          analyse_catch( e );
        } );
    };

    const add_rights = () => {
      add_rights_modal.open();
    };

    const remove_rights = item => {
      let idx = right_items.value.findIndex( it => it.value == item.value );
      right_items.value.splice( idx, 1 );
    };

    const add_rights_modal = useModal( {
      component: AddRightsModal,
      attrs: {
        rights: right_items,
        onCancel() {
          add_rights_modal.close();
        },
        onSave( selection ) {
          right_items.value = right_items.value.concat( selection );
          add_rights_modal.close();
        }
      }
    } );

    const add_users = () => {
      add_users_modal.open();
    };

    const remove_user = item => {
      let idx = user_items.value.findIndex( it => it.value == item.value );
      user_items.value.splice( idx, 1 );
    };

    const add_users_modal = useModal( {
      component: AddUsersModal,
      attrs: {
        users: user_items,
        onCancel() {
          add_users_modal.close();
        },
        onSave( selection ) {
          user_items.value = user_items.value.concat( selection );
          add_users_modal.close();
        }
      }
    } );

    const is_create_auth = () => {
      return utils.is_auth( [ API_RIGHTS.CREATE_GROUPS, API_RIGHTS.GET_RIGHTS, API_RIGHTS.GET_ACCOUNTS ] );
    };

    const is_update_auth = () => {
      return utils.is_auth( [ API_RIGHTS.UPDATE_GROUPS, API_RIGHTS.GET_RIGHTS, API_RIGHTS.GET_ACCOUNTS ] );
    };

    const is_delete_auth = () => {
      return utils.is_auth( [ API_RIGHTS.DELETE_GROUPS ] );
    };

    const is_create_or_update_auth = () => {
      return current_id && is_update_auth() || !current_id && is_create_auth();
    };

    const analyse_catch = e => {
      utils.analyse_catch( e, router, '/groups' );
    };

    initialize();

    return { t, current_id, key_rerender, is_deleteable,
      save_btn, delete_btn, input_name, input_name_required,
      breadcrumbs, input, right_headers, user_headers, right_items, user_items,
      is_create_auth, is_update_auth, is_delete_auth, is_create_or_update_auth,
      click_to_save, click_to_delete, add_rights, remove_rights, add_users, remove_user };
  }
};
</script>>